/**
 * The bridge between React and the three.js engine.
 *
 * The Engine instance is a live handle, so it lives here as a module variable
 * rather than in a store. Everything the UI can do to the scene goes through
 * this module; components never touch the engine directly.
 */

import { Engine } from "../../engine/Engine.ts";
import type { SceneTheme } from "../../engine/scene.ts";
import { setBomFromRender } from "../bom/bom.actions.ts";
import { setHistoryAvailability } from "../history/history.actions.ts";
import { setHud } from "../hud/hud.actions.ts";
import { applyViewportPlaneEdit, bindPlanesEngine, selectPlane } from "../planes/planes.actions.ts";
import { setSelectedLeg } from "../selection/selection.actions.ts";

let engine: Engine | null = null;

/** Create the engine lazily — one per page, kept across dock re-parents. */
export function getEngine(): Engine {
  if (engine) {
    return engine;
  }

  engine = new Engine({
    onRender: setBomFromRender,
    onHud: (message, sticky) => setHud(message, sticky),
    onSelectLeg: () => setSelectedLeg(engine?.selectedLeg() ?? null),
    onHistory: setHistoryAvailability,
    onPlaneEdit: applyViewportPlaneEdit,
    onPlaneSelect: selectPlane,
  });
  bindPlanesEngine(engine);

  return engine;
}

export function mountEngine(host: HTMLElement, theme: SceneTheme): () => void {
  return getEngine().mount(host, theme);
}

export function stackSelectedStandard(size: number): void {
  getEngine().stackSelected(size);
}

export function popSelectedStandard(): void {
  getEngine().popSelected();
}

export function removeSelectedStandard(): void {
  getEngine().removeSelected();
}

export function undo(): void {
  getEngine().undo();
}

export function redo(): void {
  getEngine().redo();
}

export function clearScaffold(): void {
  getEngine().clear();
}

export function cancelPending(): void {
  getEngine().cancelPending();
}

export function frameScaffold(): void {
  getEngine().frameScaffold();
}
